import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CITY_LABELS = {
  cakovec: 'Čakovec', prelog: 'Prelog', 'mursko-srediste': 'Mursko Središće',
  'donja-dubrava': 'Donja Dubrava', kotoriba: 'Kotoriba', goričan: 'Goričan',
  'mala-subotica': 'Mala Subotica', strigova: 'Štrigova', svmartin: 'Sveti Martin na Muri',
  nedelisce: 'Nedelišće', kursanec: 'Kuršanec', orehovica: 'Orehovica',
  domasinec: 'Domašinec', podturen: 'Podturen', 'sveti-juraj-u-trnju': 'Sveti Juraj u Trnju',
  'donji-kraljevec': 'Donji Kraljevec', dekanovec: 'Dekanovec', drazkovec: 'Dražkovec',
};

/**
 * City portal — shows news where title/excerpt mentions the city. Pulled
 * from the public articles search endpoint (q=cityName) and grouped into
 * a slim hero + grid layout.
 */
export default function CityPortalPage() {
  const { slug } = useParams();
  const [data, setData] = useState({ articles: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const cityName = CITY_LABELS[slug] || decodeURIComponent(slug || '').replace(/-/g, ' ');

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/public/articles?q=${encodeURIComponent(cityName)}&limit=30`)
      .then(r => r.ok ? r.json() : { articles: [] })
      .then(d => setData(d))
      .finally(() => setLoading(false));
  }, [cityName]);

  return (
    <>
      <Helmet><title>{`${cityName} — Vijesti — e-mina.eu`}</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid={`city-portal-${slug}`}>
        <div className="rounded-2xl bg-gradient-to-br from-red-600 via-rose-600 to-fuchsia-600 text-white px-6 py-8 mb-6 shadow-lg">
          <div className="flex items-center gap-3">
            <MapPin size={32} className="shrink-0"/>
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{cityName}</h1>
              <p className="text-sm text-white/80 mt-0.5">
                Sve vijesti, događaji i objave iz {cityName.startsWith('Č') ? cityCase(cityName) : cityName}.
              </p>
            </div>
          </div>
        </div>

        {loading ? <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-zinc-500" size={28}/></div> : (
          <>
            {(data.articles || []).length === 0 ? (
              <p className="text-center py-16 text-gray-500 dark:text-gray-400">
                Trenutno nema članaka za {cityName}. Vraćamo se uskoro s novim vijestima.
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {(data.articles || []).map(a => {
                  const cover = a.cover_image_url?.startsWith('http')
                    ? a.cover_image_url
                    : `${BACKEND}${a.cover_image_url || ''}`;
                  return (
                    <Link key={a.id} to={`/clanak/${a.slug}`}
                          data-testid={`city-article-${a.id}`}
                          className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                      {a.cover_image_url && (
                        <div className="aspect-[16/9] bg-gray-100 dark:bg-gray-900">
                          <img src={cover} alt="" className="w-full h-full object-cover"
                               loading="lazy" decoding="async" />
                        </div>
                      )}
                      <div className="p-4">
                        <p className="text-[10px] text-red-600 uppercase tracking-widest font-bold mb-1">{cityName}</p>
                        <h3 className="font-bold text-gray-900 dark:text-gray-100 line-clamp-2 mb-1">{a.title_hr}</h3>
                        <p className="text-xs text-gray-500 line-clamp-2">{a.excerpt_hr}</p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

function cityCase(s) {
  // Croatian-ish locative for Čakovec etc — quick and dirty fallback.
  if (s.endsWith('o')) return s + 'm';
  return s + 'u';
}
